<script setup>
import {ref, onMounted, toRef, inject, computed} from "vue";


const emit = defineEmits(['drawerClose', 'drawerOpen']);
const emitter = inject('emitter');

    const props = defineProps({
        title: { type: String, default: "" },
        name: { type: String, default: "" },
        size: { type: String, default: "default" },
        cancelButtonTitle: { default: "Cancel" },
        show: { default: false },
        showFooter: { default: true },
        showCancelButton: { default: true },
        darkOverall: { type: Boolean, default: false },
        helpScoutBeaconPresent: { type: Boolean, default: false },
    });

        const showDrawer = ref(false);
        const showProp = toRef(props, "show");
        const name = props.name;

        // Drawer size definitions
        const drawerSizes = {
            small: 'w-1/4',
            default: "w-1/3",
            large: "w-1/2",
            full: "w-screen",
        };

        const onCloseDrawer = () => {
            showDrawer.value = false;
            emit("drawerClose", name);
        };

        const onOpen = () => {
            showDrawer.value = true;
            emit("drawerOpen", name);
        };

        onMounted(() => {
            showDrawer.value = showProp.value;
            if (showDrawer.value === true) {
                onOpen();
            }

            emitter.on("drawer-on", (eventName) => {
                if (name && eventName === name) {
                    onOpen();
                }
            });

            emitter.on("drawer-off", (eventName) => {
                if (name && eventName === name) {
                    onCloseDrawer();
                }
            });
        });

        const getBackgroundColorClass = computed(() => {
            return props.darkOverall ? 'bg-gray-900 bg-opacity-75' : 'bg-gray-100 bg-opacity-50';
        });
</script>

<template>
    <div>
        <!-- Toggle Button -->
        <div @click="onOpen">
            <slot name="toggle"></slot>
        </div>

        <!-- Drawer Container -->
        <transition name="slide">
            <div v-if="showDrawer"
                 class="fixed inset-y-0 right-0 z-50 flex justify-end w-screen"
                 :class="getBackgroundColorClass"
            >
                <!-- Drawer Panel -->
                <div class="relative bg-white shadow-xl transform transition-all ease-in-out duration-500"
                     :class="[
                        { 'translate-x-0': showDrawer, 'translate-x-full': !showDrawer },
                        drawerSizes[size] || drawerSizes.default
                     ]">
                    <div class="flex h-full flex-col overflow-y-auto py-6">
                        <!-- Header -->
                        <div class="px-4 sm:px-6 flex justify-between items-center">
                            <h2 class="text-base font-semibold text-gray-900 truncate">
                                {{ title }}
                            </h2>
                            <slot name="close-button">
                                <button @click="onCloseDrawer" class="text-gray-400 hover:text-gray-500 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
                                    <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/>
                                    </svg>
                                </button>
                            </slot>
                        </div>

                        <!-- Drawer Body -->
                        <div v-if="$slots.body"
                             class="relative flex-1 px-4 sm:px-6 overflow-y-auto">
                            <slot name="body"></slot>
                        </div>

                        <!-- Footer -->
                        <div v-if="showFooter" class="py-6 px-4 bg-gray-50">
                            <div class="flex justify-end items-center space-x-3"
                                 :class="helpScoutBeaconPresent ? 'mr-24' : ''"
                            >
                                <slot v-if="showCancelButton" name="footer-cancel-button">
                                    <button @click="onCloseDrawer" class="bg-gray-200 px-4 py-2 rounded text-gray-700">
                                        {{ cancelButtonTitle }}
                                    </button>
                                </slot>
                                <slot name="footer"></slot>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<style scoped>
/* Slide in from the RIGHT - Fixed for smooth open & close */
.slide-enter-active, .slide-leave-active {
    transition: transform 0.4s ease-in-out;
}
.slide-enter, .slide-leave-to {
    transform: translateX(100%);
}
</style>
